"use client";

/**
 * WorkflowMock — animated mock of the askFinz Workflow workspace for /apps/workflow.
 *
 * Reflects the real app: a four-tab dashboard (Projects/Flows, Tool Servers,
 * API Connections, DB Connections) plus a visual flow editor.
 *
 * Animation loop:
 *   1. Start on Projects tab — flows listed with run indicators
 *   2. Switch to Tool Servers tab — servers listed with connection status
 *   3. Switch to flow editor canvas — pulse travels through nodes
 *   4. Return to Projects tab
 *
 * NOT the real Workflow builder. Lives in askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  FolderTree,
  Server,
  Plug,
  Database,
  GitBranch,
  CheckCircle2,
  Clock,
  Play,
  Sparkles,
  Cloud,
  Code2,
  RotateCcw,
  ShieldCheck,
  ShieldOff,
} from "lucide-react";

type TabKey = "projects" | "servers" | "apis" | "canvas";

type Flow = {
  id: string;
  name: string;
  description: string;
  lastRun: string;
  status: "ok" | "retry" | "idle";
};

type McpServer = {
  id: string;
  name: string;
  transport: "sse" | "stdio" | "http";
  url: string;
  connected: boolean;
};

type ApiCred = {
  id: string;
  name: string;
  header: string;
  usedBy: number;
};

const FLOWS: Flow[] = [
  { id: "f1", name: "Support ticket classifier", description: "Fetch → classify → route", lastRun: "12 min ago",  status: "ok" },
  { id: "f2", name: "Daily sales digest",         description: "Query → summarise → email", lastRun: "6 h ago",   status: "ok" },
  { id: "f3", name: "Inventory sync",             description: "Fetch → transform → upsert", lastRun: "1 h ago",  status: "retry" },
  { id: "f4", name: "News brief generator",       description: "Scrape → summarise → store", lastRun: "3 h ago",  status: "ok" },
];

const SERVERS: McpServer[] = [
  { id: "s1", name: "Search tool",   transport: "sse",   url: "search.internal/mcp",   connected: true },
  { id: "s2", name: "Browser tool",  transport: "sse",   url: "browser.internal/mcp",  connected: true },
  { id: "s3", name: "Code executor", transport: "stdio", url: "exec.internal",          connected: false },
];

const APIS: ApiCred[] = [
  { id: "a1", name: "Weather API",  header: "X-Api-Key",       usedBy: 2 },
  { id: "a2", name: "CRM webhook",  header: "Authorization",   usedBy: 3 },
  { id: "a3", name: "Slack notify", header: "Authorization",   usedBy: 1 },
];

// Canvas nodes
type WfNode = {
  id: string;
  title: string;
  detail: string;
  icon: typeof Clock;
  x: number;
  y: number;
};

const NODES: WfNode[] = [
  { id: "trigger",   title: "Trigger",   detail: "Every 15 min",       icon: Clock,    x: 8,  y: 30 },
  { id: "fetch",     title: "Fetch",     detail: "GET /tickets",       icon: Cloud,    x: 30, y: 60 },
  { id: "transform", title: "Transform", detail: "Normalise · dedupe", icon: Code2,    x: 52, y: 30 },
  { id: "classify",  title: "Classify",  detail: "AI · reasoning",     icon: Sparkles, x: 74, y: 60 },
  { id: "store",     title: "Store",     detail: "DB · upsert",        icon: Database, x: 92, y: 30 },
];

function connectorPath(a: WfNode, b: WfNode) {
  const midX = (a.x + b.x) / 2;
  return `M ${a.x} ${a.y} L ${midX} ${a.y} L ${midX} ${b.y} L ${b.x} ${b.y}`;
}

export function WorkflowMock() {
  const [tab, setTab] = useState<TabKey>("projects");
  const [activeFlowIdx, setActiveFlowIdx] = useState<number | null>(null);
  const [pulseIdx, setPulseIdx] = useState<number>(-1);
  const [doneSet, setDoneSet] = useState<Set<number>>(new Set());
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const timers: number[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    const runCycle = () => {
      // Start: projects tab
      setTab("projects");
      setActiveFlowIdx(null);
      setPulseIdx(-1);
      setDoneSet(new Set());

      // Highlight first flow
      schedule(() => setActiveFlowIdx(0), 600);

      // Switch to servers tab
      schedule(() => { setTab("servers"); setActiveFlowIdx(null); }, 2400);

      // Switch to canvas
      schedule(() => { setTab("canvas"); setPulseIdx(-1); setDoneSet(new Set()); }, 4200);

      // Walk the pulse through canvas nodes
      NODES.forEach((_, i) => {
        schedule(() => setPulseIdx(i), 4800 + i * 1000);
        schedule(() => {
          setDoneSet((prev) => {
            const n = new Set(prev);
            n.add(i);
            return n;
          });
        }, 4800 + i * 1000 + 600);
      });

      // Switch to APIs tab
      schedule(() => { setTab("apis"); setPulseIdx(-1); }, 4800 + NODES.length * 1000 + 400);

      // Loop back
      schedule(runCycle, 4800 + NODES.length * 1000 + 3000);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        schedule(runCycle, 400);
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            workflow.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span
              className={
                "h-1.5 w-1.5 rounded-full " +
                (tab === "canvas" && pulseIdx >= 0
                  ? "bg-[hsl(var(--accent))] animate-pulse"
                  : "bg-foreground/30")
              }
            />
            {tab === "canvas" && pulseIdx >= 0 ? "Running" : "Ready"}
          </span>
        </div>

        {/* Tab bar */}
        <div className="flex border-b hairline bg-foreground/[0.02]">
          {(
            [
              { key: "projects" as TabKey, icon: FolderTree, label: "Projects" },
              { key: "servers"  as TabKey, icon: Server,     label: "Tool Servers" },
              { key: "apis"     as TabKey, icon: Plug,       label: "API Keys" },
              { key: "canvas"   as TabKey, icon: GitBranch,  label: "Flow Editor" },
            ] as { key: TabKey; icon: typeof FolderTree; label: string }[]
          ).map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={
                "inline-flex items-center gap-1.5 px-4 py-2.5 text-[11px] border-b-2 transition " +
                (tab === key
                  ? "border-foreground text-foreground font-medium"
                  : "border-transparent text-foreground/55 hover:text-foreground/80")
              }
            >
              <Icon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}

          <a
            href="https://workflow.askfinz.ai/builder/"
            className="ml-auto mr-3 self-center inline-flex items-center gap-1.5 rounded-md border hairline px-2.5 py-1 text-[10px] text-foreground/65 hover:text-foreground/85 transition"
            aria-hidden
          >
            <Play className="h-2.5 w-2.5" />
            Open editor
          </a>
        </div>

        {/* Tab content */}
        <div className="min-h-[380px] bg-background">
          {/* Projects tab */}
          {tab === "projects" && (
            <div className="p-4 space-y-3 animate-[blur-fade-in_0.3s_ease-out]">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
                  {FLOWS.length} flows
                </div>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-md bg-foreground text-background px-2.5 py-1.5 text-[11px] hover:opacity-90 transition"
                >
                  <GitBranch className="h-3 w-3" />
                  New flow
                </button>
              </div>
              {FLOWS.map((f, i) => (
                <div
                  key={f.id}
                  className={
                    "rounded-xl border transition-all px-4 py-3 " +
                    (activeFlowIdx === i
                      ? "hairline-strong bg-foreground/[0.04] ring-1 ring-foreground/10"
                      : "hairline bg-card hover:hairline-strong")
                  }
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-foreground/90 truncate">{f.name}</div>
                      <div className="text-[11px] font-mono text-foreground/55 mt-0.5">{f.description}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {f.status === "ok" && <CheckCircle2 className="h-3.5 w-3.5 text-foreground/65" />}
                      {f.status === "retry" && <RotateCcw className="h-3.5 w-3.5 text-foreground/55" />}
                      {f.status === "idle" && <Clock className="h-3.5 w-3.5 text-foreground/35" />}
                      <span className="text-[10px] font-mono text-foreground/55">{f.lastRun}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Servers tab */}
          {tab === "servers" && (
            <div className="p-4 space-y-3 animate-[blur-fade-in_0.3s_ease-out]">
              <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-1">
                {SERVERS.length} tool servers registered
              </div>
              {SERVERS.map((s) => (
                <div key={s.id} className="rounded-xl border hairline bg-card px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Server className="h-4 w-4 text-foreground/55 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-foreground/90">{s.name}</div>
                      <div className="text-[10px] font-mono text-foreground/55 mt-0.5">{s.url}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="inline-flex items-center gap-1 rounded-full border hairline px-2 py-0.5 text-[9px] tracking-[0.14em] uppercase font-mono text-foreground/65">
                        {s.transport}
                      </span>
                      {s.connected
                        ? <ShieldCheck className="h-3.5 w-3.5 text-foreground/65" />
                        : <ShieldOff className="h-3.5 w-3.5 text-foreground/35" />
                      }
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* APIs tab */}
          {tab === "apis" && (
            <div className="p-4 space-y-3 animate-[blur-fade-in_0.3s_ease-out]">
              <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-1">
                {APIS.length} credentials stored
              </div>
              {APIS.map((a) => (
                <div key={a.id} className="rounded-xl border hairline bg-card px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Plug className="h-4 w-4 text-foreground/55 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-foreground/90">{a.name}</div>
                      <div className="text-[10px] font-mono text-foreground/55 mt-0.5">header: {a.header}</div>
                    </div>
                    <span className="text-[10px] font-mono text-foreground/55 shrink-0">
                      used by {a.usedBy} flows
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Canvas tab */}
          {tab === "canvas" && (
            <div className="overflow-x-auto">
            <div className="relative h-[380px] min-w-[700px] bg-background animate-[blur-fade-in_0.3s_ease-out]">
              {/* Dot grid */}
              <div
                aria-hidden
                className="absolute inset-0 opacity-50"
                style={{
                  backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
                  backgroundSize: "18px 18px",
                  color: "hsl(var(--foreground) / 0.06)",
                }}
              />

              {/* Connectors */}
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                aria-hidden
              >
                {NODES.slice(0, -1).map((n, i) => {
                  const next = NODES[i + 1];
                  const pulseHere = pulseIdx === i + 1;
                  const isDone = doneSet.has(i + 1);
                  return (
                    <g key={`c-${i}`}>
                      <path
                        d={connectorPath(n, next)}
                        fill="none"
                        stroke="currentColor"
                        strokeOpacity={isDone ? 0.4 : 0.18}
                        strokeWidth="0.35"
                        vectorEffect="non-scaling-stroke"
                      />
                      {pulseHere && (
                        <path
                          d={connectorPath(n, next)}
                          fill="none"
                          stroke="hsl(var(--accent))"
                          strokeOpacity="0.9"
                          strokeWidth="0.6"
                          strokeDasharray="3 4"
                          vectorEffect="non-scaling-stroke"
                        >
                          <animate
                            attributeName="stroke-dashoffset"
                            from="14"
                            to="0"
                            dur="0.7s"
                            repeatCount="indefinite"
                          />
                        </path>
                      )}
                    </g>
                  );
                })}
              </svg>

              {/* Nodes */}
              <div className="absolute inset-0">
                {NODES.map((n, i) => {
                  const Icon = n.icon;
                  const isActive = pulseIdx === i;
                  const isDone = doneSet.has(i);
                  return (
                    <div
                      key={n.id}
                      className="absolute -translate-x-1/2 -translate-y-1/2"
                      style={{ left: `${n.x}%`, top: `${n.y}%` }}
                    >
                      <div
                        className={
                          "rounded-xl border bg-card px-3 py-2 min-w-[110px] transition " +
                          (isActive
                            ? "hairline-strong border-[hsl(var(--accent))]/40 ring-2 ring-[hsl(var(--accent))]/15"
                            : isDone
                            ? "hairline-strong"
                            : "hairline")
                        }
                      >
                        <div className="flex items-center gap-1.5">
                          <span
                            className={
                              "inline-flex h-6 w-6 items-center justify-center rounded-md " +
                              (isActive
                                ? "bg-[hsl(var(--accent))]/12 text-[hsl(var(--accent))]"
                                : isDone
                                ? "bg-foreground/[0.06] text-foreground/85"
                                : "bg-foreground/[0.04] text-foreground/65")
                            }
                          >
                            <Icon className="h-3 w-3" />
                          </span>
                          <div className="min-w-0">
                            <div className="text-[11px] font-medium leading-tight">{n.title}</div>
                            <div className="text-[9px] font-mono text-foreground/65 leading-tight truncate">{n.detail}</div>
                          </div>
                          {isDone && !isActive && (
                            <CheckCircle2 className="h-3 w-3 text-foreground/65 ml-auto" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
